import type React from 'react'
import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import 'katex/dist/katex.min.css'
import type { MarkdownRendererProps } from '@/types/entities'

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ source, highlight }) => {
  const highlightedSource = useMemo(() => {
    // Safety check: ensure source is a string
    const text = String(source || '')

    if (!highlight.trim()) {
      return text
    }

    const searchTerm = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // Regex to match:
    // 1. Markdown links or images, capturing the parts.
    // 2. HTML tags.
    // 3. The search term itself.
    const regex = new RegExp(`(!?)\\[([^\\]]*)\\]\\(([^\\)]*)\\)|(<[^>]*>)|(${searchTerm})`, 'gi')

    return text.replace(regex, (match, isImage, linkText, linkUrl, htmlTag, searchTermMatch) => {
      // If it's an HTML tag, leave it untouched.
      if (htmlTag) {
        return match
      }

      // If it's a markdown link or image...
      if (linkText !== undefined && linkUrl !== undefined) {
        // ...highlight the search term ONLY in the link's text part.
        const highlightedText = linkText.replace(
          new RegExp(searchTerm, 'gi'),
          '<mark class="bg-orange-400 rounded">$&</mark>',
        )
        const prefix = isImage ? '!' : ''
        return `${prefix}[${highlightedText}](${linkUrl})`
      }

      // If it's a standalone search term, highlight it.
      if (searchTermMatch) {
        return `<mark class="bg-orange-400 rounded">${searchTermMatch}</mark>`
      }

      return match
    })
  }, [source, highlight])

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeRaw, rehypeKatex]}
      components={{
        a: ({ node: _, ...props }) => <a {...props} className="text-primary underline" />,
        img: ({ node: _, ...props }) => <img {...props} className="py-4" />,
      }}
    >
      {highlightedSource}
    </ReactMarkdown>
  )
}
